import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { fetchBlogPost } from "@/lib/api";
import { Clock, ArrowLeft } from "lucide-react";
import { LeadForm } from "@/components/common/LeadForm";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlogPost(slug)
      .then((data) => setPost(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-orion py-20 text-slate-500 text-sm">…</div>;
  if (error || !post) return <Navigate to="/blog" replace />;

  const title = lang === "ar" ? post.title_ar : post.title_en;
  const content = lang === "ar" ? post.content_ar : post.content_en;
  const excerpt = lang === "ar" ? post.excerpt_ar : post.excerpt_en;

  return (
    <div data-testid="blog-post-page">
      <SEO title={`${title} | ${COMPANY.brand}`} description={excerpt} />

      <article className="bg-white">
        <div className="container-orion pt-16 lg:pt-20 pb-12 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-700">
            <ArrowLeft className="w-4 h-4 rtl-flip" />
            {t("nav.blog")}
          </Link>
          <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.18em] font-semibold text-blue-700">
            <span>{post.category}</span>
            <span className="text-slate-300">·</span>
            <span className="flex items-center gap-1 text-slate-500">
              <Clock className="w-3 h-3" />
              {post.read_time} {t("blog.readingTime")}
            </span>
          </div>
          <h1 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-[1.05] text-slate-900">
            {title}
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">{excerpt}</p>
        </div>

        <div className="container-orion max-w-4xl">
          <div className="aspect-[16/8] rounded-2xl overflow-hidden bg-slate-100">
            <img src={post.image_url?.startsWith("/blog/") ? `${process.env.REACT_APP_BACKEND_URL}/api${post.image_url}` : post.image_url} alt={title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="container-orion py-16 max-w-3xl">
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 text-lg leading-relaxed whitespace-pre-line">{content}</p>
          </div>
        </div>
      </article>

      <section className="py-20 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">{t("leadForm.heading")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tighter font-medium text-slate-900 leading-tight">
              {t("contact.title")}
            </h2>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource={`blog-${slug}`} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
